import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth';
import { AppHeader, HeaderUserActions } from '@/shared/components/layout';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import type { PumsemEdition } from '../model/pumsem.type';
import { PumsemEditionSelect } from './PumsemEditionSelect';
import { PumsemSearchBar } from './PumsemSearchBar';
import { PUMSEM_SHELL } from './pumsemShell.constant';

/** 관리 진입점을 보여줄 권한. 서버도 같은 기준(ADMIN 이상)으로 막는다. */
const MANAGER_ROLES = ['ADMIN', 'SUPER_ADMIN'];

interface PumsemHeaderProps {
  query: string;
  onQueryChange: (value: string) => void;
  editions: PumsemEdition[];
  edition: string | null;
  onEditionChange: (edition: string | null) => void;
  /** 모바일 왼쪽 열(검색 결과 · 목차) 드로어 토글. */
  listOpen: boolean;
  /** 지금 그 열에 무엇이 들었는지 — `☰ 결과` / `☰ 목차`. */
  listLabel: string;
  onToggleList: () => void;
}

/**
 * 품셈해설 전용 헤더.
 *
 * 도움말과 같은 이유로 마케팅 헤더를 안 쓴다(메가메뉴가 목록·표 위로 펼쳐진다).
 * 다른 점은 **검색창이 부속이 아니라 화면의 주인공**이라는 것 — 실무자는 훑으러
 * 오는 게 아니라 특정 품으로 점프하러 온다. 그래서 트리가 아니라 검색이 헤더에
 * 상시 떠 있고, 개정판 선택이 그 옆에 붙는다.
 *
 * 바는 **뷰포트 끝까지** 쓰고 좌측 존을 결과 목록 폭에 맞춘다(`PUMSEM_SHELL`).
 * 검색창의 왼쪽 끝이 곧 상세 본문 열의 왼쪽 경계다 — 검색해서 고른 결과가
 * 바로 아래 그 자리에서 열린다는 것을 폭이 말해 준다.
 */
export function PumsemHeader({
  query,
  onQueryChange,
  editions,
  edition,
  onEditionChange,
  listOpen,
  listLabel,
  onToggleList,
}: PumsemHeaderProps) {
  const { user } = useAuth();
  const canManage = !!user?.userRole && MANAGER_ROLES.includes(user.userRole);

  return (
    <AppHeader
      position="sticky"
      width="full"
      startWidth={PUMSEM_SHELL.headerStart}
      start={
        <>
          <Link
            to={PATHS.PUMSEM}
            className="hidden shrink-0 text-[19px] font-semibold text-neutral-950/70 transition-colors hover:text-neutral-950 sm:inline dark:text-white/70 dark:hover:text-white"
          >
            품셈해설
          </Link>
          <button
            type="button"
            onClick={onToggleList}
            aria-expanded={listOpen}
            aria-controls="pumsem-result-drawer"
            className="rounded-md px-2 py-1.5 text-[15px] font-semibold text-neutral-950/70 transition-colors hover:bg-black/[0.04] lg:hidden dark:text-white/70 dark:hover:bg-white/10"
          >
            {listOpen ? '✕ 닫기' : listLabel}
          </button>
        </>
      }
      center={
        // 도움말보다 넓게 잡는다 — 모드 칩과 "품셈명 · 코드 · 직종 · 자재명"
        // 안내가 한 줄에 같이 서야 검색 대상이 무엇인지 읽힌다.
        <div className="max-w-[46rem]">
          <PumsemSearchBar value={query} onChange={onQueryChange} />
        </div>
      }
      end={
        <>
          <PumsemEditionSelect
            editions={editions}
            value={edition}
            onChange={onEditionChange}
          />
          {canManage && (
            <Link
              to={PATHS.PUMSEM_MANAGE}
              className="hidden rounded-lg border border-brand-200 px-3 py-1.5 text-[15px] font-bold text-brand-700 transition-colors hover:bg-brand-50 sm:inline-block dark:border-brand-500/30 dark:text-brand-300 dark:hover:bg-brand-500/10"
            >
              관리
            </Link>
          )}
          <HeaderUserActions showNotifications={false} />
        </>
      }
    >
      {/* 좁은 화면에서는 `center` 가 빠진다(AppHeader 규칙 — 좌우 존과 자리를
          반씩 나눠 가지면 셋 다 못 쓴다). 그런데 이 화면은 **검색이 주 진입점**
          이라 그 규칙을 그대로 따르면 모바일에서 품셈을 찾을 길이 아예 없었다.
          도움말은 ⌕ 아이콘 폴백을 쓰지만 여기서는 늘 보이는 둘째 줄로 둔다 —
          한 번 더 누르게 만들면 주 진입점이 아니게 된다. */}
      <div className="border-t border-black/[0.06] px-3.5 py-2 lg:hidden dark:border-white/8">
        <PumsemSearchBar value={query} onChange={onQueryChange} />
      </div>
    </AppHeader>
  );
}
